// src/lib/sessionHistoryQueries.js
//
// Newest-first reads of iglooSessionsHistory for one Higlo or one household.
//
// The views used to query `where(<filter>), limit(N)` with no orderBy and sort
// client-side. Firestore returns the first N matches in document-ID order and
// history IDs are random, so for a busy Higlo or household that was an
// arbitrary N sessions and the newest were often missing. Ordering in the query
// uses two composite indexes in cloud/cloudfunction/firestore.indexes.json:
//   - iglooSessionsHistory: iglooId ASC, startedAt DESC
//   - iglooSessionsHistory: participantFamilyIds CONTAINS, startedAt DESC
//
// Until those indexes are deployed and built on a project, the ordered query
// fails with `failed-precondition`. Only for that error, each call falls back
// to the view's previous query (same filter, same limit, sorted client-side),
// so the view behaves exactly as it did before the change. Any other error is
// rethrown.

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where
} from 'firebase/firestore';

const COLLECTION = 'iglooSessionsHistory';

const INDEX_DESCRIPTION = {
  iglooId: `${COLLECTION} (iglooId ASC, startedAt DESC)`,
  familyId: `${COLLECTION} (participantFamilyIds CONTAINS, startedAt DESC)`
};

const warned = new Set();

export function isMissingIndexError(err) {
  return err?.code === 'failed-precondition' && /index/i.test(err?.message || '');
}

// startedAt is written as a Timestamp; older docs may hold epoch ms.
export function startedAtMs(data) {
  const v = data?.startedAt;
  if (v == null) return -Infinity;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v === 'number') return v;
  return -Infinity;
}

function scopeKey(scope) {
  if (scope?.iglooId) return 'iglooId';
  if (scope?.familyId) return 'familyId';
  throw new Error('sessionHistoryQueries: scope must be { iglooId } or { familyId }');
}

function scopeFilter(scope) {
  return scopeKey(scope) === 'iglooId'
    ? where('iglooId', '==', scope.iglooId)
    : where('participantFamilyIds', 'array-contains', scope.familyId);
}

function warnMissingIndex(key) {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(
    `sessionHistoryQueries: Firestore index ${INDEX_DESCRIPTION[key]} is missing or still building; ` +
    'using the previous unordered query. Deploy the Firestore indexes to fix.'
  );
}

/**
 * The previous behaviour: filter + limit, no orderBy, sorted newest-first on
 * the client. Used as the fallback before the indexes exist.
 */
export async function getLegacyHistoryDocs(db, scope, legacyLimit) {
  const snap = await getDocs(query(collection(db, COLLECTION), scopeFilter(scope), limit(legacyLimit)));
  return snap.docs.slice().sort((a, b) => startedAtMs(b.data()) - startedAtMs(a.data()));
}

/**
 * Newest-first history docs for { iglooId } or { familyId }.
 *
 * @param db           Firestore (client SDK)
 * @param scope        { iglooId } | { familyId }
 * @param opts.limit   max docs from the ordered query
 * @param opts.startedAtAtMostMs  optional upper bound on startedAt (epoch ms, inclusive)
 * @param opts.legacyLimit        limit of the view's previous query, used for the fallback
 * @returns {Promise<{ docs: QueryDocumentSnapshot[], fallback: boolean }>}
 */
export async function getHistoryDocsNewestFirst(db, scope, { limit: max, startedAtAtMostMs = null, legacyLimit }) {
  const key = scopeKey(scope);
  try {
    const constraints = [scopeFilter(scope)];
    if (startedAtAtMostMs != null) {
      constraints.push(where('startedAt', '<=', Timestamp.fromMillis(startedAtAtMostMs)));
    }
    constraints.push(orderBy('startedAt', 'desc'), limit(max));
    const snap = await getDocs(query(collection(db, COLLECTION), ...constraints));
    return { docs: snap.docs, fallback: false };
  } catch (err) {
    if (!isMissingIndexError(err)) throw err;
    warnMissingIndex(key);
  }
  return { docs: await getLegacyHistoryDocs(db, scope, legacyLimit), fallback: true };
}
